<template>
  <div>
    <div v-if="$userInfo.isSupportUser || $userInfo.isCustomerAdministrator || $userInfo.isSystemAdministrator"
      class="tile is-parent pl-1">
      <article class="tile is-child box">
        <div class="columns is-flex is-align-items-center">
          <div class="column">
            <p class="title">Item Category</p>
          </div>
          <div v-if="!hasError && showSavingStatus && !toastNotificationEnabled"
            class="column is-flex is-justify-content-flex-end is-align-items-center">
            <span v-if="saving"
              class="button is-loading is-white"
              style="height: 27px;" />
            <span v-else
              class="text has-text-success">
              <span class="mso mso-18px">check_circle</span>
            </span>
            <span class="is-size-6"
              :class="saving ? 'has-text-warning' : 'has-text-success'">{{ savingStatus }}</span>
          </div>
        </div>

        <div class="mb-2 mt-5"
          :class="`has-background-${$theme.name}`">
          <div class="columns">
            <div class="column"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-2': isSuperAdmin }">
              <label class="label pl-1">Category Name</label>
            </div>
            <div class="column"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-3': isSuperAdmin }">
              <label class="label">Display Name</label>
            </div>
            <div class="column"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-3': isSuperAdmin }">
              <label class="label">Print Name</label>
            </div>
            <div class="column is-flex is-justify-content-center"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-2': isSuperAdmin }">
              <label class="label">Enabled</label>
            </div>
            <div v-if="isSuperAdmin"
              class="column is-2 is-flex is-justify-content-center">
              <label class="label">Active</label>
            </div>
          </div>
        </div>

        <div v-for="(companyItemCategory, index) in companyItemCategories"
          :key="index"
          class="item-category-row">
          <div class="field columns is-flex is-align-items-center">
            <div class="column pl-5"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-2': isSuperAdmin }">
              <div>{{ companyItemCategory.categoryName }}</div>
            </div>
            <div class="column"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-3': isSuperAdmin }">
              <input v-model="companyItemCategory.displayName"
                type="text"
                class="input">
            </div>
            <div class="column"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-3': isSuperAdmin }">
              <input v-model="companyItemCategory.printName"
                type="text"
                class="input">
            </div>
            <div class="column is-flex is-justify-content-center"
              :class="{ 'is-one-quarter': !isSuperAdmin, 'is-2': isSuperAdmin }">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger is-two-third">
                <input v-model="companyItemCategory.isEnabled"
                  type="checkbox">
                <div class="state p-primary">
                  <span class="icon mso mso-24px">check</span>
                  <label />
                </div>
              </div>
            </div>
            <div v-if="isSuperAdmin"
              class="column is-2 is-flex is-justify-content-center">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger is-two-third">
                <input v-model="companyItemCategory.isActive"
                  type="checkbox">
                <div class="state p-primary">
                  <span class="icon mso mso-24px">check</span>
                  <label />
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
    <div v-else
      class="is-flex is-justify-content-center pt-5 ">
      <div class="notification is-danger is-flex is-justify-content-center is-size-4"
        style="width: 50%">
        You do not have permissions on this page
      </div>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'
import StoreMixin from './storeMixin'
import { DeepDiff } from 'deep-diff'
import Guid from '../../components/Guid'

export default {
  name: 'ItemCategorySetting',
  mixins: [StoreMixin],
  data() {
    return {
      companyItemCategories: {},
      isNew: false
    }
  },
  watch: {
    companyItemCategories: {
      handler: function (val) {
        this.saveSnapshot({ entity: _cloneDeep(val) })
        if (typeof this.autoUpdate === 'function') this.autoUpdate(val)
      },
      deep: true
    }
  },
  computed: {
    isSuperAdmin() {
      return !this.$userInfo.isSupportUser && this.$userInfo.isSystemAdministrator
    }
  },
  async created() {
    this.$showSpinner()
    if (this.$route.params.companyId === 'new') {
      this.isNew = true
    }
    const newCompany = this.$store.getters['companysetting/newCompany']
    if (newCompany && newCompany.companyItemCategories) {
      this.companyItemCategories = _cloneDeep(newCompany.companyItemCategories)
    } else
      this.companyItemCategories = _cloneDeep(
        await this.getStoreItem({
          serviceFunction: 'getCompanyItemCategories',
          params: this.isNew ? Guid.empty() : this.$route.params.companyId
        })
      )

    if (this.isNew) this.setStateProperty({ key: 'companyItemCategories', value: this.companyItemCategories })
    this.initializeAutoUpdate()
    this.$hideSpinner()
  },
  methods: {
    disableAutoUpdate() {
      if (typeof this.autoUpdate === 'function' && typeof this.autoUpdate.cancel === 'function') {
        this.autoUpdate.cancel()
      }
      this.autoUpdate = () => {}
    },
    initializeAutoUpdate() {
      this.autoUpdate = _debounce(async (value) => {
        this.snapshotDiff && (await this.updateItemCategories(value))
      }, this.debounceDelay)
    },
    async updateItemCategories(itemCategories) {
      if (!this.snapshotDiff) return

      if (this.isNew) {
        this.setStateProperty({ key: 'companyItemCategories', value: itemCategories })
        return
      }
      for (let item of itemCategories) {
        const [baseItem] = this.baseSnapshot.filter((i) => i.itemCategoryId == item.itemCategoryId)
        const diff = DeepDiff.diff(baseItem, item)
        item.deepDiff = diff ? JSON.stringify(diff) : null
      }
      this.companyItemCategories = _cloneDeep(
        await this.editStoreItem({
          serviceFunction: 'updateCompanyItemCategories',
          entity: itemCategories,
          params: this.$route.params.companyId
        })
      )

      await this.$store.dispatch('labourTypes/getLabourTypes')

      if (this.toastNotificationEnabled) {
        this.openToastNotification()
      }
    }
  },
  async beforeRouteLeave(to, from, next) {
    this.disableAutoUpdate()
    this.toastNotificationEnabled = true
    await this.updateItemCategories(this.companyItemCategories)
    this.clearSnapshots()
    this.toastNotificationEnabled = false
    next()
  }
}
</script>

<style lang="scss" scoped>
input {
  width: 70%;
  height: 30px;
}

.item-category-row {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
  margin-bottom: 10px;
}
</style>
